/**
 * snowstorm-esm.ts
 * TypeScript (semi-typed) port of Snowstorm v1.44 converted to an instantiable class
 *
 * Notes:
 * - Public API and options are typed.
 * - Internal helpers and runtime state use pragmatic types for brevity.
 * - Targets modern browsers (no IE6/backcompat hacks required).
 */

export interface SnowOptions {
  autoStart?: boolean;
  excludeMobile?: boolean;
  flakesMax?: number;
  flakesMaxActive?: number;
  animationInterval?: number;
  useGPU?: boolean;
  className?: string | null;
  flakeBottom?: number | null;
  followMouse?: boolean;
  snowColor?: string;
  snowCharacter?: string;
  snowStick?: boolean;
  targetElement?: HTMLElement | string | null;
  useMeltEffect?: boolean;
  useTwinkleEffect?: boolean;
  usePositionFixed?: boolean;
  usePixelPosition?: boolean;

  // less used
  freezeOnBlur?: boolean;
  flakeLeftOffset?: number;
  flakeRightOffset?: number;
  flakeWidth?: number;
  flakeHeight?: number;
  vMaxX?: number;
  vMaxY?: number;
  zIndex?: number;
}

type RAF = ((cb: FrameRequestCallback) => number | void) | null;

export interface SnowFlake {
  type: number;
  x: number;
  y: number;
  vX: number;
  vY: number;
  vAmpTypes: number[];
  vAmp: number;
  melting: boolean;
  meltFrameCount: number;
  meltFrames: number[];
  meltFrame: number;
  twinkleFrame: number;
  active: number; // 1 active, 0 stuck, -1 inactive
  fontSize: number;
  o: HTMLDivElement;

  // methods:
  refresh(): boolean | void;
  stick(): void;
  vCheck(): void;
  move(): void;
  animate(): void;
  setVelocities(): void;
  setOpacity(o: HTMLElement, opacity: number): boolean | void;
  melt(): void;
  recycle(): void;
}

const gw = (typeof window !== 'undefined') ? window : (globalThis as any);
const gd = (typeof document !== 'undefined') ? document : (gw && (gw as any).document);

export class SnowStorm {
  // Public options (with defaults)
  public autoStart = false;
  public excludeMobile = false;
  public flakesMax = 128;
  public flakesMaxActive = 64;
  public animationInterval = 33;
  public useGPU = true;
  public className: string | null = null;
  public flakeBottom: number | null = null;
  public followMouse = true;
  public snowColor = '#fff';
  public snowCharacter = '&bull;';
  public snowStick = true;
  public targetElement: HTMLElement | string | null = null;
  public useMeltEffect = true;
  public useTwinkleEffect = false;
  public usePositionFixed = false;
  public usePixelPosition = false;

  // less-used options
  public freezeOnBlur = true;
  public flakeLeftOffset = 0;
  public flakeRightOffset = 0;
  public flakeWidth = 8;
  public flakeHeight = 8;
  public vMaxX = 5;
  public vMaxY = 4;
  public zIndex = 0;

  // runtime state (public read)
  public timer: boolean | null = null;
  public flakes: SnowFlake[] = [];
  public disabled = false;
  public active = false;
  public meltFrameCount = 20;
  public meltFrames: number[] = [];

  // private-ish runtime fields
  private _didInit = false;
  private _docFrag: DocumentFragment | null = gd ? gd.createDocumentFragment() : null;
  private _isIE: RegExpMatchArray | null = gw?.navigator?.userAgent?.match(/msie/i) ?? null;
  private _isIE6: RegExpMatchArray | null = gw?.navigator?.userAgent?.match(/msie 6/i) ?? null;
  private _isMobile: RegExpMatchArray | null = gw?.navigator?.userAgent?.match(/mobile|opera m(ob|in)/i) ?? null;
  private _isBackCompatIE = false;
  private _noFixed = false;

  private _screenX: number | null = null;
  private _screenX2: number | null = null;
  private _screenY: number | null = null;
  private _scrollY: number | null = null;
  private _docHeight: number | null = null;
  private _vRndX: number | null = null;
  private _vRndY: number | null = null;
  private _windOffset = 1;
  private _windMultiplier = 2;
  private _flakeTypes = 6;
  private _fixedForEverything = false;
  private _targetElementIsRelative = false;
  private _opacitySupported = true;
  private _features: { transform: { prop: string | null; }; getAnimationFrame: RAF | null; } = { transform: { prop: null }, getAnimationFrame: null };
  private _raf: RAF | null = null;

  // bound handlers for add/remove
  private _boundResizeHandler = this.resizeHandler.bind(this);
  private _boundResizeHandlerAlt = this.resizeHandlerAlt.bind(this);
  private _boundScrollHandler = this.scrollHandler.bind(this);
  private _boundMouseMove = this.mouseMove.bind(this);
  private _boundFreeze = this.freeze.bind(this);
  private _boundResume = this.resume.bind(this);
  private _boundDoStart = this._doStart.bind(this);
  private _boundDoDelayedStart = this._doDelayedStart.bind(this);
  private _boundSnowRAF?: () => void;

  constructor(options: Partial<SnowOptions> = {}) {
    // assign provided options
    Object.assign(this, options);

    // compat flags
    this._isBackCompatIE = !!(this._isIE && gd && gd.compatMode === 'BackCompat');
    this._noFixed = !!(this._isBackCompatIE || this._isIE6);

    // detect opacity support
    try {
      const d = gd.createElement('div');
      d.style.opacity = '0.5';
      this._opacitySupported = true;
    } catch (e) {
      this._opacitySupported = false;
    }

    // compute feature support
    this._features = this._computeFeatures();
    this._raf = this._features.getAnimationFrame ?? null;

    // precompute melt frames
    for (let i = 0; i < this.meltFrameCount; i++) {
      this.meltFrames.push(1 - (i / this.meltFrameCount));
    }

    // autoStart hook
    if (this.autoStart && typeof gw !== 'undefined' && typeof gd !== 'undefined') {
      this._addEvent(gw, 'load', this._boundDoStart);
    }
  }

  // ---------------- feature detection ----------------
  private _computeFeatures() {
    const testDiv = gd.createElement('div');
    const has = (prop: string) => (testDiv.style[prop as any] !== undefined ? prop : null);

    const timeoutShim: RAF = (cb) => {
      return (gw.setTimeout as any)(cb, 1000 / (this.animationInterval || 20));
    };

    const _animationFrame = (gw.requestAnimationFrame ||
      (gw as any).webkitRequestAnimationFrame ||
      (gw as any).mozRequestAnimationFrame ||
      (gw as any).oRequestAnimationFrame ||
      (gw as any).msRequestAnimationFrame ||
      timeoutShim) as RAF;

    const getAnimationFrame: RAF = _animationFrame ? function (cb: FrameRequestCallback) {
      // @ts-ignore apply signature
      return _animationFrame.apply(gw, [cb]);
    } : null;

    const localFeatures = {
      transform: {
        ie: has('-ms-transform'),
        moz: has('MozTransform'),
        opera: has('OTransform'),
        webkit: has('webkitTransform'),
        w3: has('transform'),
        prop: null as string | null
      },
      getAnimationFrame
    };

    localFeatures.transform.prop = (
      (localFeatures.transform.w3 as string) ||
      (localFeatures.transform.moz as string) ||
      (localFeatures.transform.webkit as string) ||
      (localFeatures.transform.ie as string) ||
      (localFeatures.transform.opera as string) ||
      null
    );

    return localFeatures;
  }

  // ---------------- basic event helpers ----------------
  private _addEvent(el: any, type: string, handler: any, capture = false) {
    if (!el) return;
    if (el.addEventListener) el.addEventListener(type, handler, capture);
    else if (el.attachEvent) el.attachEvent('on' + type, handler);
  }

  private _removeEvent(el: any, type: string, handler: any, capture = false) {
    if (!el) return;
    if (el.removeEventListener) el.removeEventListener(type, handler, capture);
    else if (el.detachEvent) el.detachEvent('on' + type, handler);
  }

  // ---------------- randomness and wind ----------------
  private _rnd(n: number, min?: number) {
    if (isNaN(min as any)) min = 0;
    return (Math.random() * n) + (min as number);
  }
  private _plusMinus(n: number) {
    return (parseInt(String(this._rnd(2)), 10) === 1 ? n * -1 : n);
  }

  public randomizeWind() {
    this._vRndX = this._plusMinus(this._rnd(this.vMaxX, 0.2));
    this._vRndY = this._rnd(this.vMaxY, 0.2);
    if (this.flakes) {
      for (let i = 0; i < this.flakes.length; i++) {
        if (this.flakes[i].active) {
          this.flakes[i].setVelocities();
        }
      }
    }
  }

  // ---------------- handlers ----------------
  public scrollHandler() {
    this._scrollY = (this.flakeBottom ? 0 : parseInt(String((gw.scrollY || gd.documentElement.scrollTop || (this._noFixed ? gd.body.scrollTop : 0))), 10));
    if (isNaN(this._scrollY as any)) this._scrollY = 0;
    if (!this._fixedForEverything && !this.flakeBottom && this.flakes) {
      for (let i = 0; i < this.flakes.length; i++) {
        if (this.flakes[i].active === 0) {
          this.flakes[i].stick();
        }
      }
    }
  }

  public resizeHandler() {
    if (gw.innerWidth || gw.innerHeight) {
      this._screenX = (gw.innerWidth as number) - 16 - this.flakeRightOffset;
      this._screenY = (this.flakeBottom !== null ? this.flakeBottom : gw.innerHeight as number);
    } else {
      this._screenX = ((gd.documentElement.clientWidth || (gd.body as any).clientWidth || (gd.body as any).scrollWidth) as number) - (!this._isIE ? 8 : 0) - this.flakeRightOffset;
      this._screenY = (this.flakeBottom !== null ? this.flakeBottom : (gd.documentElement.clientHeight || (gd.body as any).clientHeight || (gd.body as any).scrollHeight) as number);
    }
    this._docHeight = gd.body.offsetHeight;
    this._screenX2 = parseInt(String((this._screenX as number) / 2), 10);
  }

  public resizeHandlerAlt() {
    if (!this.targetElement || typeof this.targetElement === 'string') return;
    this._screenX = (this.targetElement as HTMLElement).offsetWidth - this.flakeRightOffset;
    this._screenY = this.flakeBottom !== null ? this.flakeBottom : (this.targetElement as HTMLElement).offsetHeight;
    this._screenX2 = parseInt(String((this._screenX as number) / 2), 10);
    this._docHeight = gd.body.offsetHeight;
  }

  public freeze() {
    if (!this.disabled) {
      this.disabled = true;
    } else {
      return false;
    }
    this.timer = null;
  }

  public resume() {
    if (this.disabled) {
      this.disabled = false;
    } else {
      return false;
    }
    this.timerInit();
  }

  public toggleSnow() {
    if (!this.flakes.length) {
      this.start();
    } else {
      this.active = !this.active;
      if (this.active) {
        this.show();
        this.resume();
      } else {
        this.stop();
        this.freeze();
      }
    }
  }

  public stop() {
    this.freeze();
    for (let i = 0; i < this.flakes.length; i++) {
      this.flakes[i].o.style.display = 'none';
    }
    this._removeEvent(gw, 'scroll', this._boundScrollHandler);
    this._removeEvent(gw, 'resize', this._boundResizeHandler);
    if (this.freezeOnBlur) {
      if (this._isIE) {
        this._removeEvent(gd, 'focusout', this._boundFreeze);
        this._removeEvent(gd, 'focusin', this._boundResume);
      } else {
        this._removeEvent(gw, 'blur', this._boundFreeze);
        this._removeEvent(gw, 'focus', this._boundResume);
      }
    }
  }

  public show() {
    for (let i = 0; i < this.flakes.length; i++) {
      this.flakes[i].o.style.display = 'block';
    }
  }

  // ---------------- SnowFlake factory ----------------
  // create a SnowFlake record with methods bound to this instance
  private SnowFlake(type: number, x?: number, y?: number): SnowFlake {
    const storm = this;
    const s = {
      type,
      x: x ?? parseInt(String(this._rnd((this._screenX ?? 0) - 20)), 10),
      y: (!isNaN(y as any) ? (y as number) : -this._rnd((this._screenY ?? 0)) - 12),
      vX: 0,
      vY: 0,
      vAmpTypes: [1, 1.2, 1.4, 1.6, 1.8] as number[],
      vAmp: 1,
      melting: false,
      meltFrameCount: storm.meltFrameCount,
      meltFrames: storm.meltFrames,
      meltFrame: 0,
      twinkleFrame: 0,
      active: 1,
      fontSize: 10,
      o: gd.createElement('div') as HTMLDivElement,

      // methods will be added below
      refresh(): boolean | void { return; },
      stick(): void { return; },
      vCheck(): void { return; },
      move(): void { return; },
      animate(): void { return; },
      setVelocities(): void { return; },
      setOpacity(o: HTMLElement, opacity: number): boolean | void { return; },
      melt(): void { return; },
      recycle(): void { return; },
    } as unknown as SnowFlake;

    // finalize initial properties
    s.vAmp = s.vAmpTypes[s.type] ?? 1;
    s.fontSize = (10 + (s.type / 5) * 10);
    s.o.innerHTML = storm.snowCharacter;
    if (storm.className) s.o.setAttribute('class', storm.className);
    s.o.style.color = storm.snowColor;
    s.o.style.position = (storm._fixedForEverything ? 'fixed' : 'absolute');
    if (storm.useGPU && storm._features.transform.prop) {
      (s.o.style as any)[storm._features.transform.prop] = 'translate3d(0px, 0px, 0px)';
    }
    s.o.style.width = storm.flakeWidth + 'px';
    s.o.style.height = storm.flakeHeight + 'px';
    s.o.style.fontFamily = 'arial,verdana';
    s.o.style.cursor = 'default';
    s.o.style.overflow = 'hidden';
    s.o.style.fontWeight = 'normal';
    s.o.style.zIndex = String(storm.zIndex);

    if (storm._docFrag) storm._docFrag.appendChild(s.o);

    s.refresh = function () {
      if (isNaN(s.x as any) || isNaN(s.y as any)) return false;
      storm.setXY(s.o, s.x, s.y);
    };

    s.stick = function () {
      if (storm._noFixed || (storm.targetElement !== gd.documentElement && storm.targetElement !== gd.body)) {
        s.o.style.top = ((storm._screenY ?? 0) + (storm._scrollY ?? 0) - storm.flakeHeight) + 'px';
      } else if (storm.flakeBottom !== null) {
        s.o.style.top = (storm.flakeBottom as number) + 'px';
      } else {
        s.o.style.display = 'none';
        s.o.style.bottom = '0%';
        s.o.style.position = 'fixed';
        s.o.style.display = 'block';
      }
    };

    s.vCheck = function () {
      if (s.vX >= 0 && s.vX < 0.2) s.vX = 0.2;
      else if (s.vX < 0 && s.vX > -0.2) s.vX = -0.2;
      if (s.vY >= 0 && s.vY < 0.2) s.vY = 0.2;
    };

    s.move = function () {
      const vX = s.vX * storm._windOffset;
      s.x += vX;
      s.y += (s.vY * s.vAmp);
      if (s.x >= (storm._screenX ?? 0) || (storm._screenX! - s.x) < storm.flakeWidth) {
        s.x = 0;
      } else if (vX < 0 && s.x - storm.flakeLeftOffset < -storm.flakeWidth) {
        s.x = (storm._screenX ?? 0) - storm.flakeWidth - 1;
      }
      s.refresh();
      const yDiff = (storm._screenY ?? 0) + (storm._scrollY ?? 0) - s.y + storm.flakeHeight;
      if (yDiff < storm.flakeHeight) {
        s.active = 0;
        if (storm.snowStick) s.stick();
        else s.recycle();
      } else {
        if (storm.useMeltEffect && s.active && s.type < 3 && !s.melting && Math.random() > 0.998) {
          s.melting = true;
          s.melt();
        }
        if (storm.useTwinkleEffect) {
          if (s.twinkleFrame < 0) {
            if (Math.random() > 0.97) s.twinkleFrame = parseInt(String(Math.random() * 8), 10);
          } else {
            s.twinkleFrame--;
            if (!storm._opacitySupported) {
              s.o.style.visibility = (s.twinkleFrame && s.twinkleFrame % 2 === 0 ? 'hidden' : 'visible');
            } else {
              s.o.style.opacity = (s.twinkleFrame && s.twinkleFrame % 2 === 0 ? '0' : '1');
            }
          }
        }
      }
    };

    s.animate = function () { s.move(); };

    s.setVelocities = function () {
      s.vX = (storm._vRndX ?? 0) + storm._rnd(storm.vMaxX * 0.12, 0.1);
      s.vY = (storm._vRndY ?? 0) + storm._rnd(storm.vMaxY * 0.12, 0.1);
    };

    s.setOpacity = function (o: HTMLElement, opacity: number) {
      if (!storm._opacitySupported) return false;
      o.style.opacity = String(opacity);
    };

    s.melt = function () {
      if (!storm.useMeltEffect || !s.melting) {
        s.recycle();
      } else {
        if (s.meltFrame < s.meltFrameCount) {
          s.setOpacity(s.o, s.meltFrames[s.meltFrame]);
          s.o.style.fontSize = (s.fontSize - (s.fontSize * (s.meltFrame / s.meltFrameCount))) + 'px';
          s.o.style.lineHeight = (storm.flakeHeight + 2 + (storm.flakeHeight * 0.75 * (s.meltFrame / s.meltFrameCount))) + 'px';
          s.meltFrame++;
        } else {
          s.recycle();
        }
      }
    };

    s.recycle = function () {
      s.o.style.display = 'none';
      s.o.style.position = (storm._fixedForEverything ? 'fixed' : 'absolute');
      s.o.style.bottom = 'auto';
      s.setVelocities();
      s.vCheck();
      s.meltFrame = 0;
      s.melting = false;
      s.setOpacity(s.o, 1);
      s.o.style.padding = '0px';
      s.o.style.margin = '0px';
      s.o.style.fontSize = s.fontSize + 'px';
      s.o.style.lineHeight = (storm.flakeHeight + 2) + 'px';
      s.o.style.textAlign = 'center';
      s.o.style.verticalAlign = 'baseline';
      s.x = parseInt(String(storm._rnd((storm._screenX ?? 0) - storm.flakeWidth - 20)), 10);
      s.y = parseInt(String(storm._rnd((storm._screenY ?? 0)) * -1), 10) - storm.flakeHeight;
      s.refresh();
      s.o.style.display = 'block';
      s.active = 1;
    };

    // initial placement
    s.recycle();
    s.refresh();

    return s;
  }

  // ---------------- place flake element ----------------
  public setXY(o: HTMLElement, x: number, y: number) {
    if (!o) return false;
    if (this.usePixelPosition || this._targetElementIsRelative) {
      o.style.left = (x - this.flakeWidth) + 'px';
      o.style.top = (y - this.flakeHeight) + 'px';
    } else if (this._noFixed) {
      o.style.right = (100 - (x / (this._screenX ?? 1) * 100)) + '%';
      o.style.top = (Math.min(y, (this._docHeight ?? 0) - this.flakeHeight)) + 'px';
    } else {
      if (!this.flakeBottom) {
        o.style.right = (100 - (x / (this._screenX ?? 1) * 100)) + '%';
        o.style.bottom = (100 - (y / (this._screenY ?? 1) * 100)) + '%';
      } else {
        o.style.right = (100 - (x / (this._screenX ?? 1) * 100)) + '%';
        o.style.top = (Math.min(y, (this._docHeight ?? 0) - this.flakeHeight)) + 'px';
      }
    }
  }

  // ---------------- create flakes and append to DOM ----------------
  public createSnow(limit: number, allowInactive?: boolean) {
    for (let i = 0; i < limit; i++) {
      const flake = this.SnowFlake(parseInt(String(this._rnd(this._flakeTypes)), 10));
      this.flakes.push(flake);
      if (allowInactive || i > this.flakesMaxActive) {
        this.flakes[this.flakes.length - 1].active = -1;
      }
    }

    if (this.targetElement && this._docFrag) {
      if (typeof this.targetElement === 'string') {
        const el = gd.getElementById(this.targetElement);
        (el || (gd.body || gd.documentElement)).appendChild(this._docFrag);
      } else {
        (this.targetElement as HTMLElement).appendChild(this._docFrag);
      }
    } else if (this._docFrag) {
      (gd.body || gd.documentElement).appendChild(this._docFrag);
    }
  }

  // ---------------- main animation loop ----------------
  private _snow = () => {
    let active = 0;
    for (let i = 0; i < this.flakes.length; i++) {
      if (this.flakes[i].active === 1) {
        this.flakes[i].move();
        active++;
      }
      if (this.flakes[i].melting) {
        this.flakes[i].melt();
      }
    }
    if (active < this.flakesMaxActive) {
      const flake = this.flakes[parseInt(String(this._rnd(this.flakes.length)), 10)];
      if (flake && flake.active === 0) {
        flake.melting = true;
      }
    }
    if (this.timer) {
      const raf = this._raf;
      if (raf !== null) {
        raf(this._boundSnowRAF ?? (this._boundSnowRAF = this._snow));
      }
    }
  };

  public timerInit() {
    this.timer = true;
    this._snow();
  }

  public init() {
    this.randomizeWind();
    this.createSnow(this.flakesMax);
    this._addEvent(gw, 'resize', this._boundResizeHandler);
    this._addEvent(gw, 'scroll', this._boundScrollHandler);
    if (this.freezeOnBlur) {
      if (this._isIE) {
        this._addEvent(gd, 'focusout', this._boundFreeze);
        this._addEvent(gd, 'focusin', this._boundResume);
      } else {
        this._addEvent(gw, 'blur', this._boundFreeze);
        this._addEvent(gw, 'focus', this._boundResume);
      }
    }
    this.resizeHandler();
    this.scrollHandler();
    if (this.followMouse) {
      this._addEvent(this._isIE ? gd : gw, 'mousemove', this._boundMouseMove);
    }
    this.animationInterval = Math.max(20, this.animationInterval);
    this.timerInit();
  }

  // ---------------- start / stop ----------------
  public start(bFromOnLoad?: boolean) {
    if (!this._didInit) {
      this._didInit = true;
    } else if (bFromOnLoad) {
      return true;
    }

    if (typeof this.targetElement === 'string') {
      const id = this.targetElement;
      const el = gd.getElementById(id);
      if (!el) throw new Error('SnowStorm: Unable to get targetElement "' + id + '"');
      this.targetElement = el;
    }

    if (!this.targetElement) this.targetElement = (gd.body || gd.documentElement) as any;
    if (this.targetElement !== gd.documentElement && this.targetElement !== gd.body) {
      this.resizeHandler = this.resizeHandlerAlt;
      this.usePixelPosition = true;
    }
    this.resizeHandler();
    this.usePositionFixed = (this.usePositionFixed && !this._noFixed && !this.flakeBottom);
    if (gw.getComputedStyle) {
      try {
        this._targetElementIsRelative = (gw.getComputedStyle(this.targetElement as Element, null).getPropertyValue('position') === 'relative');
      } catch (e) {
        this._targetElementIsRelative = false;
      }
    }
    this._fixedForEverything = this.usePositionFixed;
    if (this._screenX && this._screenY && !this.disabled) {
      this.init();
      this.active = true;
    }
  }

  private _doDelayedStart() {
    gw.setTimeout(() => {
      this.start(true);
    }, 20);
    this._removeEvent(this._isIE ? gd : gw, 'mousemove', this._boundDoDelayedStart);
  }

  private _doStart() {
    if (!this.excludeMobile || !this._isMobile) {
      this._doDelayedStart();
    }
    this._removeEvent(gw, 'load', this._boundDoStart);
  }

  public mouseMove(e: MouseEvent | any) {
    if (!this.followMouse) return true;
    const x = parseInt(String(e.clientX), 10);
    if (x < (this._screenX2 ?? 0)) {
      this._windOffset = -this._windMultiplier + (x / (this._screenX2 ?? 1) * this._windMultiplier);
    } else {
      const nx = x - (this._screenX2 ?? 0);
      this._windOffset = (nx / (this._screenX2 ?? 1)) * this._windMultiplier;
    }
  }

  // ---------------- helper to expose instance as window global ----------------
  public static attachToWindow(name = 'snowStorm', instance?: SnowStorm) {
    if (typeof gw === 'undefined') return;
    const inst = instance ?? new SnowStorm();
    (gw as any)[name] = inst;
    return inst;
  }
}

export default SnowStorm;
